/**
 * ESM compatibility utilities for __dirname and __filename
 *
 * In ES modules, __dirname and __filename are not available.
 * Use these helpers to get equivalent values.
 *
 * NOTE: These utilities are isomorphic - they work in both Node.js and browsers.
 * In browsers, they return the URL string as-is since file paths don't apply.
 *
 * @example
 * import { getDirname, getFilename , isMainModule } from 'shared/utils/esm';
 * const __dirname = getDirname(import.meta.url);
 * const __filename = getFilename(import.meta.url);
 */

// Check if we're in Node.js environment
const isNode =
	typeof process !== 'undefined' &&
	process.versions != null &&
	process.versions.node != null;

/**
 * Convert a file:// URL to a file path (works in Node.js)
 * In browsers, just returns the URL as-is
 */
const fileURLToPath = (url) => {
	if (!isNode) {
		return typeof url === 'string' ? url : '';
	}

	// Handle URL objects
	const urlString = typeof url === 'string' ? url : url.href;

	if (!urlString.startsWith('file://')) {
		return urlString;
	}

	// Remove file:// prefix and decode URI components
	let path = decodeURIComponent(urlString.slice(7));

	// Handle Windows paths (file:///C:/path)
	if (path.match(/^\/[A-Za-z]:\//)) {
		path = path.slice(1);
	}

	return path;
};

/**
 * Get the directory part of a path
 */
const pathDirname = (filePath) => {
	if (!isNode) {
		if (typeof filePath !== 'string') return '';
		const lastSlash = filePath.lastIndexOf('/');
		return lastSlash >= 0 ? filePath.substring(0, lastSlash) : filePath;
	}

	// For Node.js, handle both forward and back slashes
	const lastSlash = Math.max(
		filePath.lastIndexOf('/'),
		filePath.lastIndexOf('\\'),
	);
	return lastSlash >= 0 ? filePath.substring(0, lastSlash) : filePath;
};

/**
 * Resolve symlinks (Node.js only, returns input in browsers)
 */
const fsRealpathSync = (filePath) => {
	if (!isNode) return filePath;
	try {
		// Dynamic require inside a function that only runs in Node.js
		// eslint-disable-next-line global-require, import/no-dynamic-require
		return require('fs').realpathSync(filePath);
	} catch {
		return filePath;
	}
};

/**
 * Get the directory name of the current module (equivalent to __dirname in CommonJS)
 * @param {string} importMetaUrl - Pass `import.meta.url` from the calling module
 * @returns {string} The directory path of the calling module (or URL directory in browsers)
 */
export const getDirname = (importMetaUrl) =>
	pathDirname(fileURLToPath(importMetaUrl));

/**
 * Get the file name of the current module (equivalent to __filename in CommonJS)
 * @param {string} importMetaUrl - Pass `import.meta.url` from the calling module
 * @returns {string} The file path of the calling module (or URL in browsers)
 */
export const getFilename = (importMetaUrl) => fileURLToPath(importMetaUrl);

/**
 * Check if the current module is the main entry point (equivalent to isMainModule(import.meta.url) in CommonJS)
 * @param {string} importMetaUrl - Pass `import.meta.url` from the calling module
 * @returns {boolean} True if this module is being run directly (always false in browsers)
 */
export const isMainModule = (importMetaUrl) => {
	if (!isNode) return false;
	// In bundled executables (bun build --compile), module-level test code should never run
	if (process.env.BUN_COMPILED_EXECUTABLE === 'true') return false;
	const scriptPath = fileURLToPath(importMetaUrl);
	const argvPath = process.argv[1];
	if (!argvPath) return false;
	// Direct match
	if (argvPath === scriptPath) return true;
	// Resolve symlinks for proper comparison
	try {
		return fsRealpathSync(argvPath) === fsRealpathSync(scriptPath);
	} catch {
		return false;
	}
};
